import { FC, useEffect } from 'react';
import * as tsClient from '@tsClient';
import Spacer from '@components/Spacer/Spacer';
import useNoodleApi from '@hooks/useNoodleApi';
import ProgressIndicator from '@components/ProgressIndicator';
import InvoiceSettings from '@components/InvoiceSettings';
import { useToast } from '@/hooks';
import { useEventsContext } from '@/providers/Events';
import useSocketContext from '@/providers/Socket/useSocketContext';
import { SocketMessageType } from '@/providers/Socket';

export type AttachPriceToInvoiceProps = {
  onSubmitCompleted?: () => Promise<void> | void;
  invoiceId: string;
};

const AttachPriceToInvoice: FC<AttachPriceToInvoiceProps> = ({ invoiceId, onSubmitCompleted }) => {
  const { emit } = useEventsContext();
  const {joinChannels} = useSocketContext();
  const { data: invoice, getData: getInvoice } = useNoodleApi(tsClient.invoices.getInvoice);
  const { getData: setLineItemsFn } = useNoodleApi(tsClient.invoices.setInvoiceLineItems, {
    toastOnError: true,
  });
  const { getData: updateInvoiceFn } = useNoodleApi(tsClient.invoices.updateInvoice, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Invoice updated successfully!'],
  });

  useEffect(() => {
    getInvoice({ id: invoiceId });
  }, [getInvoice, invoiceId]);

  const handleSubmit = async ({ invoiceData, lineItems }: { invoiceData: Omit<Parameters<typeof tsClient.invoices.updateInvoice>[0], 'id'>; lineItems: Parameters<typeof tsClient.invoices.setInvoiceLineItems>[0]['lineItems'] }): Promise<void> => {
    await updateInvoiceFn({
      ...invoiceData,
      id: invoiceId,
    })
      .then(() => {
        emit('refreshUserWorkflowList', {});
        joinChannels([SocketMessageType.USER_WORKFLOW_UPDATED]);
      });
    await setLineItemsFn({ id: invoiceId, lineItems });
    await onSubmitCompleted?.();
  };

  return (
    <>
      <Spacer size={16} />
      {!invoice && <ProgressIndicator isCentered />}
      {invoice && (
        <InvoiceSettings requireLineItems isInvoice currency={invoice.currency} onSave={handleSubmit} initialValue={invoice} />
      )}
    </>
  );
};

export default AttachPriceToInvoice;
